import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { CheckCircle2, XCircle, Clock, Package } from "lucide-react";
import { formatCurrency } from "@/lib/format";
import { useSettings } from "@/hooks/useSettings";

const OrderConfirmation = () => {
  const { id } = useParams();
  const [params] = useSearchParams();
  const status = params.get("status");
  const [order, setOrder] = useState<any>(null);
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const settings = useSettings();
  const symbol = settings.currency_symbol || "৳";

  useEffect(() => {
    const load = async () => {
      const { data: o } = await supabase.from("orders").select("*").eq("id", id).maybeSingle();
      const { data: it } = await supabase.from("order_items").select("*").eq("order_id", id);
      setOrder(o);
      setItems(it ?? []);
      setLoading(false);
    };
    load();
  }, [id]);

  if (loading) {
    return <div className="container py-16"><Skeleton className="h-96 rounded-2xl" /></div>;
  }
  if (!order) return <div className="container py-16 text-center">Order not found.</div>;

  const failed = status === "failed" || status === "cancelled";
  const paid = order.payment_status === "paid";

  return (
    <div className="container py-12 max-w-2xl">
      <div className="rounded-3xl bg-card shadow-card p-8 md:p-12 text-center">
        {failed ? (
          <>
            <XCircle className="h-16 w-16 mx-auto text-destructive mb-4" />
            <h1 className="font-display text-3xl font-bold mb-2">Payment {status}</h1>
            <p className="text-muted-foreground">Your order is saved. You can retry payment from your order history.</p>
          </>
        ) : paid ? (
          <>
            <CheckCircle2 className="h-16 w-16 mx-auto text-success mb-4" />
            <h1 className="font-display text-3xl font-bold mb-2">Thank you!</h1>
            <p className="text-muted-foreground">Your order has been confirmed.</p>
          </>
        ) : (
          <>
            <Clock className="h-16 w-16 mx-auto text-warning mb-4" />
            <h1 className="font-display text-3xl font-bold mb-2">Order received</h1>
            <p className="text-muted-foreground">Payment is pending. We'll update you once confirmed.</p>
          </>
        )}

        <div className="mt-8 text-left rounded-2xl bg-secondary/50 p-6 space-y-3">
          <div className="flex justify-between text-sm"><span className="text-muted-foreground">Order ID</span><span className="font-mono text-xs">{order.id.slice(0, 8)}</span></div>
          <div className="flex justify-between text-sm"><span className="text-muted-foreground">Status</span><span className="capitalize">{order.order_status}</span></div>
          <div className="flex justify-between text-sm"><span className="text-muted-foreground">Payment</span><span className="capitalize">{order.payment_status}</span></div>
          <div className="border-t pt-3 space-y-2">
            {items.map((i) => (
              <div key={i.id} className="flex justify-between text-sm">
                <span>{i.product_name} × {i.quantity}</span>
                <span>{formatCurrency(Number(i.price) * i.quantity, symbol)}</span>
              </div>
            ))}
          </div>
          <div className="border-t pt-3 flex justify-between font-display text-lg font-bold">
            <span>Total</span>
            <span>{formatCurrency(Number(order.total_amount), symbol)}</span>
          </div>
        </div>

        <div className="flex gap-3 mt-8 justify-center">
          <Button variant="outline" asChild><Link to="/">Continue shopping</Link></Button>
          <Button variant="hero" asChild><Link to="/orders"><Package className="h-4 w-4" /> My orders</Link></Button>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmation;
